import { getCsrfTokenGlobal } from 'context';
import { ApiEndpoints } from 'models/api';
import { standardApi } from 'store/api';
import { env } from 'utils/env';

interface ChatRequest {
	prompt: string;
}

interface ChatResponse {
	response: string;
	tokens_used: number;
}

// Types for AI Usage / Token Tracking
interface TokenUsageEntry {
	date: string;
	input_tokens: number;
	output_tokens: number;
	total_tokens: number;
	request_count: number;
	healthsystem_name?: string;
	user_email?: string;
}

interface AIUsageTableResponse {
	stats: TokenUsageEntry[];
	total_input_tokens: number;
	total_output_tokens: number;
	total_tokens: number;
	total_requests: number;
}

interface AIUsageGraphResponse {
	data_points: Array<{
		x: string;
		y: number;
		input_tokens?: number;
		output_tokens?: number;
	}>;
	total_input_tokens: number;
	total_output_tokens: number;
	total_tokens: number;
}

interface AIUsageTableRequest {
	start_date: string;
	end_date: string;
	healthsystem_id: number;
	sort?: Array<{ id: string; desc: boolean }>;
}

interface AIUsageGraphRequest {
	start_date: string;
	end_date: string;
	group: string; // 'daily', 'weekly', 'monthly'
	healthsystem_id: number;
}

// Types for conversations endpoint
interface Conversation {
	conversation_id: string;
	user_id: number;
	user_email?: string | null;
	user_name?: string | null;
	facility_id?: number | null;
	healthsystem_id?: number | null;
	facility_name?: string | null;
	healthsystem_name?: string | null;
	title: string;
	created_at: string;
	last_updated_at: string;
}

// Types for single conversation endpoint
interface Message {
	attachments?: ChatAttachment[];
	message_id: number;
	conversation_id: string;
	sender_role: string;
	message_text: string;
	created_at: string;
	tool_call_id?: string | null;
	tool_name?: string | null;
	feedback?: 'thumbsUp' | 'thumbsDown' | null;
}

interface GetConversationResponse {
	conversation: Conversation;
	messages: Message[];
	message_count: number;
}

interface PaginationInfo {
	total_items: number;
	page: number;
	per_page: number;
	total_pages: number;
	has_next: boolean;
	has_prev: boolean;
}

interface GetConversationsResponse {
	conversations: Conversation[];
	count: number;
	has_next: boolean;
	has_prev: boolean;
	limit: number;
	page: number;
	total_count: number;
	total_pages: number;
}

interface GetConversationsArgs {
	page?: number;
	limit?: number;
	global?: boolean;
	user_ids?: number[];
}

// Types for feedback endpoint
export interface FeedbackRequest {
	conversation_id: string;
	message_id?: number;
	feedback: 'thumbsUp' | 'thumbsDown';
}

interface FeedbackResponse {
	success: boolean;
	message: string;
	conversation_id: string;
	feedback: string;
}

// Types for search conversations endpoint
interface SearchConversationsArgs {
	q: string;
	limit?: number;
	global?: boolean;
	user_ids?: number[];
}

interface SearchConversationsResponse {
	results: Array<
		Conversation & {
			matching_messages: Message[];
			matching_message_count: number;
		}
	>;
	query: string;
	count: number;
	total_count: number;
	limit: number;
}

type ChatStreamArgs = {
	submission: ChatSubmission;
	chatId?: string;
	jobId?: string;
	bypassPacing?: boolean;
	bypassPacingUntilReset?: boolean;
	requestId: number;
	selectedSystem?: number | null;
	selectedFacility?: number | null;
	currentFacility?: any;
	currentHealthSystem?: any;
	availableFacilities?: any[];
	earliest_date?: string;
	end_date?: string;
	start_date?: string;
	surgeons?: any[];
	serviceLines?: any[];
	rooms?: any[];
	encounterTypes?: any[];
	blockNames?: any[];
};

type ChatStreamData = {
	errorMessage?: string;
	text: string;
	done: boolean;
	rateLimitMessage?: string;
	chatId?: string;
	jobId?: string;
	queueStatus?: string;
	queuePosition?: number;
	activeStreams?: number;
	pollAfterMs?: number;
	isToolRunning: boolean;
	currentTool?: { name: string; args?: any };
	retryAttempt?: number;
	requestId?: number; // Track which request generated this response
};

const MAX_STREAM_RECOVERY_RETRIES = 12;
const STREAM_RECOVERY_BASE_DELAY_MS = 1000;

const getRecoveryDelayMs = (retryNumber: number) => {
	const jitterMs = Math.floor(Math.random() * 250);
	return Math.min(STREAM_RECOVERY_BASE_DELAY_MS * 2 ** Math.max(0, retryNumber - 1) + jitterMs, 10000);
};

const createAbortError = () => {
	const abortErr = new Error('Aborted');
	(abortErr as any).name = 'AbortError';
	return abortErr;
};

const waitWithAbort = (ms: number, signal: AbortSignal) =>
	new Promise<void>((resolve, reject) => {
		if (signal.aborted) {
			reject(createAbortError());
			return;
		}

		const timeoutId = window.setTimeout(() => {
			signal.removeEventListener('abort', onAbort);
			resolve();
		}, ms);

		const onAbort = () => {
			window.clearTimeout(timeoutId);
			signal.removeEventListener('abort', onAbort);
			reject(createAbortError());
		};

		signal.addEventListener('abort', onAbort);
	});

const isRetriableStreamingError = (error: any, statusCode?: number) => {
	if (error?.name === 'AbortError') return false;

	if (typeof statusCode === 'number') {
		if (statusCode === 408) return true;
		if (statusCode >= 500) return true;
		if (statusCode >= 400) return false;
	}

	const message = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase();
	return (
		error instanceof TypeError ||
		message.includes('failed to fetch') ||
		message.includes('networkerror') ||
		message.includes('err_connection_reset') ||
		message.includes('network') ||
		message.includes('socket') ||
		message.includes('stream ended before terminal done')
	);
};

/**
 * Holds AbortControllers for active streams, keyed by requestId.
 */
const streamControllers = new Map<number, AbortController>();

export interface ChatAttachment {
	id: number;
	filename: string;
	mime_type: string;
	size_bytes: number;
	created_at: string;
}

export interface ChatSubmission {
	text: string;
	attachments: ChatAttachment[];
}

export const attachmentUrl = (id: number) => `${(env('API_BASE') ?? '').replace(/\/$/, '')}/llm/attachments/${id}`;

export const uploadChatAttachment = async (file: File): Promise<ChatAttachment> => {
	const body = new FormData();
	body.append('file', file);
	// Let the browser set the multipart boundary; uploads use the same cookie/CSRF auth as streams.
	const token = getCsrfTokenGlobal();
	const response = await fetch(`${(env('API_BASE') ?? '').replace(/\/$/, '')}/llm/attachments`, {
		method: 'POST',
		body,
		credentials: 'include',
		headers: token ? { 'X-CSRF-Token': token } : {},
	});
	if (!response.ok) {
		// Attachment validation uses plain-text errors; auth middleware may return JSON instead.
		const bodyText = await response.text();
		let detail = '';
		try {
			const parsed = JSON.parse(bodyText);
			detail = parsed?.message || parsed?.error || '';
		} catch {
			if (response.headers.get('content-type')?.includes('text/plain')) detail = bodyText;
		}
		throw new Error(detail || `Attachment upload failed (${response.status}). Please try again.`);
	}
	return response.json();
};

/**
 * Public helper to cancel a running stream from the UI.
 */
export const cancelChatStream = (requestId: number) => {
	const ctrl = streamControllers.get(requestId);
	if (ctrl) {
		ctrl.abort();
		streamControllers.delete(requestId);
	}
};

export const llmService = standardApi.injectEndpoints({
	endpoints: (build) => ({
		chat: build.mutation<ChatResponse, ChatRequest>({
			query: (body) => ({
				url: ApiEndpoints.LLM_CHAT,
				method: 'POST',
				body: { prompt: body.prompt },
			}),
		}),

		getConversations: build.query<GetConversationsResponse, GetConversationsArgs>({
			query: (args) => {
				const params = new URLSearchParams();
				if (args.page) params.append('page', args.page.toString());
				if (args.limit) params.append('limit', args.limit.toString());
				if (args.global) params.append('global', 'true');
				args.user_ids?.forEach((userId) => params.append('user_ids', userId.toString()));

				const queryString = params.toString();
				return {
					url: queryString ? `${ApiEndpoints.LLM_CONVERSATIONS}?${queryString}` : ApiEndpoints.LLM_CONVERSATIONS,
					method: 'GET',
				};
			},
		}),

		getConversation: build.query<GetConversationResponse, string>({
			query: (conversationId) => ({
				url: `${ApiEndpoints.LLM_CONVERSATION}/${conversationId}`,
				method: 'GET',
			}),
		}),

		searchConversations: build.query<SearchConversationsResponse, SearchConversationsArgs>({
			query: (args) => {
				const params = new URLSearchParams();
				params.append('q', args.q);
				if (args.limit) params.append('limit', args.limit.toString());
				if (args.global) params.append('global', 'true');
				args.user_ids?.forEach((userId) => params.append('user_ids', userId.toString()));

				return {
					url: `${ApiEndpoints.LLM_CONVERSATIONS_SEARCH}?${params.toString()}`,
					method: 'GET',
				};
			},
		}),

		submitFeedback: build.mutation<FeedbackResponse, FeedbackRequest>({
			query: (body) => ({
				url: ApiEndpoints.LLM_FEEDBACK,
				method: 'POST',
				body: {
					conversation_id: body.conversation_id,
					message_id: body.message_id,
					feedback: body.feedback,
				},
			}),
		}),

		chatStream: build.query<ChatStreamData, ChatStreamArgs>({
			keepUnusedDataFor: 0,

			// Initial state for stream data
			queryFn: (arg) => ({
				data: {
					text: '',
					done: false,
					chatId: undefined,
					isToolRunning: false,
					currentTool: undefined,
					retryAttempt: 0,
					requestId: arg.requestId,
				},
			}),

			async onQueryStarted(arg, { dispatch }) {
				const patch = (updater: (d: ChatStreamData) => void) =>
					dispatch(
						llmService.util.updateQueryData('chatStream', arg, (draft) => {
							draft.requestId = arg.requestId;
							updater(draft);
						})
					);

				// Set initial requestId in the data so consumers can validate
				patch((d) => {
					d.requestId = arg.requestId;
					d.retryAttempt = 0;
				});

				// Create + register one lifecycle controller so UI cancel can abort retries too.
				const lifecycleCtrl = new AbortController();
				streamControllers.set(arg.requestId, lifecycleCtrl);

				let latestJobId = arg.jobId;
				let latestChatId = arg.chatId;
				let latestQueueStatus: string | undefined;
				const isQueuedStatus = (status?: string) => status === 'queued' || status === 'waiting';

				try {
					const apiBaseUrl = env('API_BASE');

					for (let retryCount = 0; retryCount <= MAX_STREAM_RECOVERY_RETRIES; retryCount += 1) {
						let sawDoneEvent = false;
						let receivedSseFrame = false;
						let terminalEvent: any;

						try {
							const response = await fetch(`${apiBaseUrl}${ApiEndpoints.LLM_CHAT_STREAM}`, {
								method: 'POST',
								headers: {
									'Content-Type': 'application/json',
									...(getCsrfTokenGlobal() ? { 'X-CSRF-Token': getCsrfTokenGlobal()! } : {}),
								},
								credentials: 'include',
								body: JSON.stringify({
									message: arg.submission.text,
									attachment_ids: arg.submission.attachments.map((image) => image.id),
									chatId: latestChatId,
									jobId: latestJobId,
									bypassPacing: arg.bypassPacing === true,
									bypassPacingUntilReset: arg.bypassPacingUntilReset === true,
									requestId: arg.requestId,
									selectedSystem: arg.selectedSystem,
									selectedFacility: arg.selectedFacility,
									currentFacility: arg.currentFacility,
									currentHealthSystem: arg.currentHealthSystem,
									availableFacilities: arg.availableFacilities,
									earliest_date: arg.earliest_date,
									end_date: arg.end_date,
									start_date: arg.start_date,
									surgeons: arg.surgeons,
									serviceLines: arg.serviceLines,
									rooms: arg.rooms,
									encounterTypes: arg.encounterTypes,
									blockNames: arg.blockNames,
								}),
								signal: lifecycleCtrl.signal,
							});

							if (!response.ok) {
								if (response.status === 429) {
									const message =
										(await response.text().catch(() => '')).trim() ||
										'You have reached your usage limit. Reload the page to try again.';
									patch((d) => {
										d.rateLimitMessage = message;
										d.done = true;
										d.isToolRunning = false;
										d.currentTool = undefined;
										d.retryAttempt = 0;
									});
									return;
								}

								const httpError = new Error(`HTTP error! status: ${response.status}`) as Error & { status?: number };
								httpError.status = response.status;
								throw httpError;
							}

							const reader = response.body?.getReader();
							if (!reader) throw new Error('Response body is not readable');

							const decoder = new TextDecoder();
							let buffer = '';

							const handleEvent = (eventName: string, evt: any): boolean => {
								receivedSseFrame = true;
								const normalizedType = evt?.type || eventName;

								switch (normalizedType) {
									case 'heartbeat':
										// keepalive signal only; no UI mutation needed
										break;

									case 'queue':
										latestJobId = evt.job_id ?? latestJobId;
										latestQueueStatus = evt.status;
										patch((d) => {
											d.jobId = evt.job_id ?? d.jobId;
											d.queueStatus = evt.status;
											d.queuePosition = evt.position;
											d.activeStreams = evt.active_streams;
											d.pollAfterMs = evt.poll_after_ms;
											d.retryAttempt = 0;
											if (evt.status === 'queued' || evt.status === 'waiting') {
												d.done = false;
												d.isToolRunning = false;
												d.currentTool = undefined;
											}
										});
										break;

									case 'meta':
										if (evt.chat_id) {
											latestChatId = evt.chat_id;
											patch((d) => {
												d.chatId = evt.chat_id;
												d.retryAttempt = 0;
											});
										}
										break;

									case 'token':
										latestQueueStatus = 'running';
										patch((d) => {
											d.text += evt.text ?? '';
											d.done = false;
											d.retryAttempt = 0;
										});
										break;

									case 'tool_start':
										patch((d) => {
											d.isToolRunning = true;
											d.currentTool = { name: evt.tool, args: evt.args };
											d.retryAttempt = 0;
										});
										break;

									case 'tool_end':
										patch((d) => {
											d.isToolRunning = false;
											d.currentTool = undefined;
										});
										break;

									case 'done':
										sawDoneEvent = true;
										terminalEvent = evt;
										if (evt.chat_id) latestChatId = evt.chat_id;
										if (evt.job_id) latestJobId = evt.job_id;
										return true;

									case 'error':
										patch((d) => {
											d.errorMessage = evt.message ?? 'Unknown error';
											d.text += `\nError: ${evt.message ?? 'Unknown error'}\n`;
											d.done = false;
											d.isToolRunning = false;
											d.currentTool = undefined;
										});
										break;

									default:
										break;
								}

								return false;
							};

							const processSseFrame = (frame: string): boolean => {
								const lines = frame.split('\n');
								let eventName = 'message';
								const dataLines: string[] = [];

								for (const rawLine of lines) {
									const line = rawLine.replace(/\r$/, '');
									if (!line || line.startsWith(':')) continue;
									if (line.startsWith('event:')) {
										eventName = line.slice(6).trim() || 'message';
										continue;
									}
									if (line.startsWith('data:')) {
										dataLines.push(line.slice(5).trimStart());
									}
								}

								if (dataLines.length === 0) return false;
								const dataStr = dataLines.join('\n');
								let evt: any = null;

								try {
									evt = JSON.parse(dataStr);
								} catch {
									evt = { type: eventName, text: dataStr };
								}

								return handleEvent(eventName, evt);
							};

							try {
								while (!sawDoneEvent) {
									const { done, value } = await reader.read();
									if (done) break;

									buffer += decoder.decode(value, { stream: true });

									const frames = buffer.split('\n\n');
									buffer = frames.pop() ?? '';
									for (const frame of frames) {
										if (!frame.trim()) continue;
										if (processSseFrame(frame)) break;
									}
								}

								// Flush an unterminated final frame only when the transport reached EOF.
								if (!sawDoneEvent && buffer.length) {
									processSseFrame(buffer);
								}
							} finally {
								if (sawDoneEvent) {
									try {
										await reader.cancel();
									} catch {
										// The server may have already closed the stream after its terminal event.
									}
								}
								reader.releaseLock();
							}

							if (sawDoneEvent) {
								patch((d) => {
									d.done = true;
									d.isToolRunning = false;
									d.currentTool = undefined;
									d.chatId = terminalEvent?.chat_id ?? d.chatId;
									d.jobId = terminalEvent?.job_id ?? d.jobId;
									d.retryAttempt = 0;
								});
							}

							patch((d) => {
								// Stream EOF should not mark completion for queued poll responses.
								// Only terminal stream completions should set done=true.
								d.done = d.queueStatus === 'queued' || d.queueStatus === 'waiting' ? false : true;
								d.isToolRunning = false;
								d.currentTool = undefined;
								d.retryAttempt = 0;
							});

							if (!sawDoneEvent) {
								if (isQueuedStatus(latestQueueStatus)) {
									return;
								}
								throw new Error(receivedSseFrame ? 'Stream ended before terminal done event' : 'Stream ended unexpectedly');
							}

							return;
						} catch (error: any) {
							if (error?.name === 'AbortError') {
								patch((d) => {
									d.text += `\n[stream canceled]\n`;
									d.done = true;
									d.isToolRunning = false;
									d.currentTool = undefined;
								});
								return;
							}

							const statusCode = typeof error?.status === 'number' ? error.status : undefined;
							const shouldRetry =
								!isQueuedStatus(latestQueueStatus) &&
								retryCount < MAX_STREAM_RECOVERY_RETRIES &&
								isRetriableStreamingError(error, statusCode);

							if (shouldRetry) {
								const delayMs = getRecoveryDelayMs(retryCount + 1);
								patch((d) => {
									d.done = false;
									d.isToolRunning = false;
									d.currentTool = undefined;
									d.retryAttempt = retryCount + 1;
								});

								try {
									await waitWithAbort(delayMs, lifecycleCtrl.signal);
								} catch (waitError: any) {
									if (waitError?.name === 'AbortError') {
										patch((d) => {
											d.text += `\n[stream canceled]\n`;
											d.done = true;
											d.isToolRunning = false;
											d.currentTool = undefined;
										});
										return;
									}
									throw waitError;
								}

								continue;
							}

							console.error('Streaming error:', error);
							patch((d) => {
								d.errorMessage = error instanceof Error ? error.message : 'Unknown streaming error';
								d.text += `\nError: ${error instanceof Error ? error.message : 'Unknown streaming error'}\n`;
								d.done = true;
								d.isToolRunning = false;
								d.currentTool = undefined;
							});
							return;
						}
					}
				} finally {
					streamControllers.delete(arg.requestId);
				}
			},
		}),
	}),
});

export const {
	useChatMutation,
	useChatStreamQuery,
	useGetConversationsQuery,
	useGetConversationQuery,
	useSearchConversationsQuery,
	useSubmitFeedbackMutation,
} = llmService;

export const {
	endpoints: { chat, chatStream, submitFeedback },
} = llmService;
